import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { useFlags } from '@fullstory/react-hooks';
import { ComputePageFlowResponse, QueryCacheOptions } from '@fullstory/proto/fs/services/alexa';
import { PageFlowComputation } from '@fullstory/proto/fs/api/metrics/pagemetrics';
import { fullstorySelectors, searchMetaSelectors } from '../../selectors';
import { AppDispatch, AppState } from '../../types';
import { AppCacheStatus } from '@fullstory/redux-util/utils';
import { PageFlowActions, PageFlowAsyncActions } from './actions';
import { metricsCacheOptionsRefresh } from '../metrics';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { useReleaseAlexaBackgroundRefs } from '../../utils';
import { UID } from '@fullstory/util/uid';

const { getSearchCacheKey } = searchMetaSelectors;
const { getOrgId } = fullstorySelectors;

export type PageFlowComputationSettings = {
  storageId: string;
  computation: PageFlowComputation;
};

/**
 * Performs a set of computations and returns some helpers that
 * make it easier to work with the set.
 * @param computationToFetch - Page flow computation to fetch
 * @param isEnabled - If `false`, requests will not be dispatched. Useful for preventing
 * requests until some condition is met.
 * @param automaticallyUseNewestData - If `true`, the hook will automatically use the newest data in redux.
 */
export const usePageFlowComputation = ({
  computationToFetch,
  isEnabled = true,
  automaticallyUseNewestData,
}: {
  computationToFetch: PageFlowComputationSettings | null;
  isEnabled: boolean;
  automaticallyUseNewestData: boolean;
}) => {
  const usePageFlowComputationSetRet = usePageFlowComputationSet(
    computationToFetch,
    isEnabled,
    automaticallyUseNewestData,
  );
  const usePageFlowComputationResultsRet = usePageFlowComputationResults(
    computationToFetch,
    isEnabled,
  );

  return useMemo(
    () => ({
      ...usePageFlowComputationSetRet,
      ...usePageFlowComputationResultsRet,
    }),
    [usePageFlowComputationResultsRet, usePageFlowComputationSetRet],
  );
};

const usePageFlowComputationSet = (
  computationToFetch: PageFlowComputationSettings | null,
  isEnabled: boolean,
  forceSwap: boolean = false,
) => {
  const flags = useFlags();
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const searchCacheKey = useSelector(getSearchCacheKey);

  const fetchComputation = useCallback(
    (cacheOptions?: QueryCacheOptions, isRefresh = false) => {
      if (computationToFetch) {
        const uid = UID();

        const appCache = PageFlowAsyncActions.appCache(uid, forceSwap);
        const storageId = computationToFetch.storageId;
        appCache.action(
          dispatch,
          storageId,
          {
            body: { orgId, computation: computationToFetch.computation, cacheOptions },
            flags,
            searchCacheKey,
          },
          isRefresh,
        );
        return {
          ...appCache,
          storageId,
          uid,
        };
      }
      return null;
    },
    [orgId, computationToFetch, searchCacheKey, dispatch, flags, forceSwap],
  );

  useEffect(() => {
    let request: ReturnType<typeof fetchComputation> | undefined;
    if (isEnabled) {
      request = fetchComputation();
    }
    return () => request?.cancel(dispatch, request.uid);
  }, [fetchComputation, dispatch, isEnabled]);

  const forceRefresh = useCallback(
    () => fetchComputation(metricsCacheOptionsRefresh, true),
    [fetchComputation],
  );

  return {
    /**
     * Function that, when called, triggers a forced refresh of the page flow computation.
     */
    forceRefresh,
  };
};

const usePageFlowComputationResults = (
  computationToFetch: PageFlowComputationSettings | null,
  isEnabled: boolean,
) => {
  const pageData = useSelector((state: AppState) => state.pages.pageMetrics);
  const dispatch = useDispatch<AppDispatch>();
  const storageId = computationToFetch?.storageId ?? '';

  // The ResourceCacheButton shows the status with the highest priority (HasData > Fresh). Because of this we can get into a state where 1 or more
  // storage ids has new data in the app cache but the other storage ids have fresh data. If the user clicks the cache button, we only want to
  // swap the cache for the stale storage id. Swapping the cache for the other ids will inadvertantly wipe out the cached data.
  const forceCacheSwap = useCallback(() => {
    if (pageData[storageId]?.status == AppCacheStatus.HasData) {
      dispatch(PageFlowActions.appCacheSwap(storageId));
    }
  }, [storageId, dispatch, pageData]);

  const { pageFlowResults, pageFlowStatus, timestamp } = useMemo(() => {
    if (isEmpty(pageData) || isEmpty(computationToFetch)) return {};

    const pageFlowResponse = {
      data: pageData[storageId]?.data,
      status: pageData[storageId]?.status,
    };

    return {
      pageFlowResults: {
        data: pageFlowResponse.data,
      },
      pageFlowStatus: pageFlowResponse.status,
      timestamp: Timestamp.toDate(pageFlowResponse?.data?.computedAt),
    };
  }, [computationToFetch, storageId, pageData]);

  const isValid = computationToFetch != null;

  const data: ComputePageFlowResponse = useMemo(() => {
    if (
      isValid &&
      pageFlowResults &&
      pageFlowResults.data &&
      (pageFlowStatus == AppCacheStatus.Fresh || pageFlowStatus == AppCacheStatus.HasData)
    ) {
      return pageFlowResults.data;
    }
    return {
      pageFlow: null,
      computedAt: Timestamp.nil,
      backgroundRefId: '',
      sampleResults: null,
    };
  }, [pageFlowResults, isValid, pageFlowStatus]);

  const orgId = useSelector(fullstorySelectors.getOrgId);
  const backgroundRefIds = data.backgroundRefId ? [data.backgroundRefId] : [];
  useReleaseAlexaBackgroundRefs(orgId, backgroundRefIds, !isEnabled);

  return {
    ...data,
    status: pageFlowStatus ?? AppCacheStatus.Initial,
    /**
     * Function that, when called, triggers a cache swap of the page flow computation(s) that is out of date.
     */
    forceCacheSwap,
    timestamp,
  };
};
